import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { Menu, X } from "lucide-react";

const HEADER_OFFSET = 80; // sticky header (h-16 = 64) + breathing room

const Header = () => {
  const { t, locale, routes, switchLocale } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState("home");

  // Identify which top-level page we're on
  const isHome = location.pathname === routes.home;
  const isContact = location.pathname === routes.contact;

  // Scroll-spy: only on home page
  const computeActive = useCallback(() => {
    const desEl = document.getElementById("desinfection");
    const audienceEl = document.getElementById("pour-qui");
    const scrollY = window.scrollY + HEADER_OFFSET + 1;

    const desTop = desEl ? desEl.getBoundingClientRect().top + window.scrollY : Infinity;
    const audienceTop = audienceEl ? audienceEl.getBoundingClientRect().top + window.scrollY : Infinity;

    // Bottom-of-page guard: if user is near page bottom, keep the last nav anchor active.
    const nearBottom =
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;

    let next = "home";
    if (scrollY >= desTop) next = "desinfection";
    if (scrollY >= audienceTop) next = "pourQui";
    if (nearBottom && audienceEl) next = "pourQui";
    setActiveAnchor(next);
  }, []);

  useEffect(() => {
    if (!isHome) {
      setActiveAnchor(null);
      return;
    }
    computeActive();
    const onScroll = () => computeActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isHome, computeActive, location.pathname, location.hash]);

  const navItems = [
    { key: "home", to: routes.home, label: t.nav.home, anchor: "home" },
    { key: "desinfection", to: routes.desinfection, label: t.nav.desinfection, anchor: "desinfection" },
    { key: "pourQui", to: routes.pourQui, label: t.nav.pourQui, anchor: "pourQui" },
    { key: "contact", to: routes.contact, label: t.nav.contact, page: "contact" },
  ];

  const isItemActive = (it) => {
    if (it.page === "contact") return isContact;
    // Anchor items are only active on the home page based on scroll position
    if (!isHome) return false;
    return activeAnchor === it.anchor;
  };

  const linkClass = (active) =>
    `px-3 py-2 text-sm font-medium transition-colors ${
      active ? "text-[#016C36]" : "text-slate-700 hover:text-slate-950"
    }`;

  return (
    <header
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to={routes.home} className="flex items-center group" data-testid="logo-link">
          <img
            src="/safemat-logo-dark.png"
            alt="SAFEMAT"
            className="h-14 w-auto -my-2 transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((it) => {
            const active = isItemActive(it);
            return (
              <Link
                key={it.key}
                to={it.to}
                className={linkClass(active)}
                aria-current={active ? "page" : undefined}
                data-testid={`nav-${it.key}`}
                data-active={active ? "true" : "false"}
              >
                {it.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={switchLocale}
            className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-950 transition-colors"
            data-testid="lang-switch-btn"
          >
            <span className="text-[#016C36] font-semibold">{locale.toUpperCase()}</span>
            <span className="opacity-30">/</span>
            <span>{t.switchLang}</span>
          </button>
          <Link
            to={routes.quote}
            className="btn-primary hidden sm:inline-flex !py-2 !px-4 !text-xs"
            data-testid="header-cta-btn"
          >
            {t.nav.cta}
          </Link>
          <button
            className="md:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {navItems.map((it) => {
              const active = isItemActive(it);
              return (
                <Link
                  key={it.key}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  className={`py-2.5 text-sm font-medium ${
                    active ? "text-[#016C36]" : "text-slate-700"
                  }`}
                  data-testid={`nav-mobile-${it.key}`}
                  data-active={active ? "true" : "false"}
                >
                  {it.label}
                </Link>
              );
            })}
            <button
              onClick={() => {
                switchLocale();
                setOpen(false);
              }}
              className="mt-3 self-start text-xs font-medium text-slate-500"
            >
              <span className="text-[#016C36] font-semibold">{locale.toUpperCase()}</span>
              <span className="opacity-30 mx-1">/</span>
              <span>{t.switchLang}</span>
            </button>
            <Link
              to={routes.quote}
              onClick={() => setOpen(false)}
              className="btn-primary mt-3 self-start"
            >
              {t.nav.cta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
